import * as React from "react";
import { useContext } from "react";
import { Formik, Form, Field } from "formik";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { validateField, formHasErrors } from "util/FormsUtils";
import { FormikFormGroup, Select, InputList, TimezoneSelect } from "components/common";
import { Alert, Button, ButtonToolbar, Row, Col, Panel, Input } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import BackendWizardContext from "./BackendWizardContext";
export const STEP_KEY = "user-synchronization";
export const FORM_VALIDATION = {
  defaultRoles: { required: true },
  defaultUserTimezone: {},
  userFullNameAttribute: { required: true },
  userNameAttribute: { required: true },
  emailAttributes: {},
  userSearchBase: { required: true },
  userSearchPattern: { required: true },
  userUniqueIdAttribute: {}
};
const StyledInputList = styled(InputList)`
  margin: auto 15px;
`;
const UserSyncStep = ({
  help = {},
  excludedFields = {},
  formRef,
  onSubmit,
  onSubmitAll,
  submitAllError,
  validateOnMount,
  roles
}) => {
  const { setStepsState, ...stepsState } = useContext(BackendWizardContext);
  const { backendValidationErrors } = stepsState;
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id })).toArray();
  const sendTelemetry = useSendTelemetry("directory-service");
  const { t } = useTranslation("authentication");
  const _onSubmitAll = (validateForm) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_USER_SYNC_SAVE_CLICKED, {
      app_action_value: "usersync-save"
    });
    validateForm().then((errors) => {
      if (!formHasErrors(errors)) {
        onSubmitAll();
      }
    });
  };
  const getInitalFormValues = (values) => ({
    ...values,
    ...!excludedFields.emailAttributes && { emailAttributes: values.emailAttributes || [] }
  });
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: getInitalFormValues(stepsState.formValues),
      initialErrors: backendValidationErrors,
      innerRef: formRef,
      onSubmit,
      validateOnBlur: false,
      validateOnChange: false,
      validateOnMount
    },
    ({ isSubmitting, validateForm }) => /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userSearchBase,
        label: t("user_sync.search_base_dn"),
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userSearchBase,
        name: "userSearchBase",
        placeholder: t("user_sync.search_base_dn"),
        validate: validateField(FORM_VALIDATION.userSearchBase)
      }
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userSearchPattern,
        label: t("user_sync.search_pattern"),
        name: "userSearchPattern",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userSearchPattern,
        placeholder: t("user_sync.search_pattern"),
        validate: validateField(FORM_VALIDATION.userSearchPattern)
      }
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userNameAttribute,
        label: t("user_sync.name_attribute"),
        name: "userNameAttribute",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userNameAttribute,
        placeholder: t("user_sync.name_attribute"),
        validate: validateField(FORM_VALIDATION.userNameAttribute)
      }
    ), !excludedFields.emailAttributes && /* @__PURE__ */ React.createElement(Field, { name: "emailAttributes", validate: validateField(FORM_VALIDATION.emailAttributes) }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        bsStyle: error ? "error" : void 0,
        help: help.emailAttributes,
        error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.emailAttributes,
        id: "email-attributes-input",
        label: t("user_sync.email_attributes"),
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(
        StyledInputList,
        {
          id: "userEmailAttributes",
          placeholder: t("user_sync.email_attributes"),
          name,
          values: value,
          isClearable: true,
          onChange
        }
      )
    )), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userFullNameAttribute,
        label: t("user_sync.full_name_attribute"),
        name: "userFullNameAttribute",
        placeholder: t("user_sync.full_name_attribute"),
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userFullNameAttribute,
        validate: validateField(FORM_VALIDATION.userFullNameAttribute)
      }
    ), !excludedFields.userUniqueIdAttribute && /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.userUniqueIdAttribute,
        label: t("user_sync.id_attribute"),
        name: "userUniqueIdAttribute",
        placeholder: t("user_sync.id_attribute"),
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.userUniqueIdAttribute,
        validate: validateField(FORM_VALIDATION.userUniqueIdAttribute)
      }
    ), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 9, smOffset: 3 }, /* @__PURE__ */ React.createElement(Panel, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "user_sync.default_assignments_hint",
        values: { serviceTitle: stepsState.authBackendMeta.serviceTitle }
      }
    )))), /* @__PURE__ */ React.createElement(Field, { name: "defaultRoles", validate: validateField(FORM_VALIDATION.defaultRoles) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        bsStyle: error ? "error" : void 0,
        help: help.defaultRoles,
        error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.defaultRoles,
        id: "default-roles-select",
        label: t("user_sync.default_roles"),
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(
        Select,
        {
          multi: true,
          onBlur,
          onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
          options: rolesOptions,
          placeholder: t("user_sync.search_roles"),
          value
        }
      )
    )), /* @__PURE__ */ React.createElement(Field, { name: "defaultUserTimezone" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "default-user-timezone-select",
        help: help.defaultUserTimezone,
        label: t("user_sync.default_user_time_zone"),
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(
        TimezoneSelect,
        {
          value: value || "Browser's time zone",
          name: "timezone",
          onChange: (newValue) => onChange({ target: { name, value: newValue } })
        }
      )
    )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 9, smOffset: 3 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, t("user_sync.test_login_hint")))), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(Button, { disabled: isSubmitting, onClick: () => _onSubmitAll(validateForm), type: "button" }, t("wizard.finish_and_save_identity_service")), /* @__PURE__ */ React.createElement(
      Button,
      {
        bsStyle: "primary",
        disabled: isSubmitting,
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_NEXT_GROUP_SYNC_CLICKED, {
            app_action_value: "groupsync-button"
          });
        },
        type: "submit"
      },
      t("wizard.next_group_sync")
    )))
  );
};
export default UserSyncStep;
